import { useSelector } from "react-redux";
import { enterpriseLevelsDetailsSelector } from "../Reducers/enterpriseLevelsDetailsReducer";
import { useFetchCompanyInfosQuery } from "../Apis/companyApi";
import { useMemo } from "react";

export const NODE_TYPE = {
  ENTERPRISE: "ENTERPRISE",
  REGION: "REGION",
  SUB_REGION: "SUB_REGION",
  COMPANY: "COMPANY",
}

export class Node {
  constructor(id, name, type) {
    this.id = id;
    this.name = name;
    this.type = type;
    this.children = [];
    this.companyCount = (type === NODE_TYPE.COMPANY) ? 1 : 0;
  }

  addChild(child) {
    this.children.push(child);
  }

  getChildren() {
    return this.children;
  }

  getId() {
    return this.id;
  }

  getName() {
    return this.name;
  }

  getType() {
    return this.type;
  }

  dfs(keepEmptyNodes = false) {
    for (let child of this.children) {
      child.dfs(keepEmptyNodes);
      this.companyCount += child.companyCount;
    }

    this.children = this.children.filter((child) => {
      return keepEmptyNodes || child.companyCount > 0;
    });
  }
}


export function useEnterpriseStructure(keepEmptyNodes = false) {
  const { data: companyInfoMap } = useFetchCompanyInfosQuery();
  const enterpriseLevelsDetails = useSelector(enterpriseLevelsDetailsSelector);

  const tree = useMemo(() => {
    if (!companyInfoMap || !enterpriseLevelsDetails) {
      return null;
    }

    const regionLevel = new Map();
    const root = new Node(
      enterpriseLevelsDetails.getEnterprise().getId(),
      enterpriseLevelsDetails.getEnterprise().getName(),
      NODE_TYPE.ENTERPRISE
    );

    for (let region of enterpriseLevelsDetails.getRegions()) {
      let regionNode = new Node(NODE_TYPE.REGION, region.getName(), region.getId());
      regionLevel.set(region.getId(), regionNode);
      root.addChild(regionNode);
    }

    for (let subregion of enterpriseLevelsDetails.getSubregions()) {
      let subregionNode = new Node(NODE_TYPE.SUB_REGION, subregion.getName(), subregion.getId());
      regionLevel.get(subregion.regionId)?.addChild(subregionNode);

      for (let companyId of subregion.getCompanies()) {
        const company = companyInfoMap.getAccountDetailsByCompanyId(companyId);
        if (!company) continue;

        let companyNode = new Node(company.getCompanyId(), company.getCompanyName(), NODE_TYPE.COMPANY);
        subregionNode.addChild(companyNode);
      }
    }

    // TODO: Incorporate directlyAssignedCompanies from /admin/info — attach companies
    // that are not under any subregion to the enterprise root (or a dedicated node).

    root.dfs(keepEmptyNodes);

    return root;
  }, [companyInfoMap, enterpriseLevelsDetails, keepEmptyNodes]);


  return {
    tree: tree,
    companies: companyInfoMap?.getAccountsArray() ?? [],
  };
}
